import gql from "graphql-tag";
import Moment from "moment";
import { extendMoment } from "moment-range";
const moment = extendMoment(Moment);

/**
 *
 * @class JourneyCalculator
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.5] app/src/model/JourneyCalculator.js
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default class JourneyCalculator {
  constructor(
    apolloMobileClient,
    questionnaireManager,
    patientId,
    enrollmentDate,
  ) {
    /** @type ApolloClient */
    this._apolloMobileClient = apolloMobileClient;
    this._questionnaireManager = questionnaireManager;
    this._patientId = patientId;
    this._enrollmentDate = enrollmentDate;


    this.myBehaviourComponentLogs = null;
    this.overTimeChartBehaviourGoalsData = [[], [], [], [], []];
    this.sliderValue = null;
    this.visibleJourneyProgress = null;
    this.journeyProgress = null;
  }


  // Load all behaviourComponent changes
  async loadMyBehaviourComponentLogs() {
    return this._apolloMobileClient
      .query({
        query: gql`
          query myBehaviourComponentLogs($patientId: String!) {
              myBehaviourComponentLogs(
                  orders: [{ field: createdAt, direction: asc }]
                  functions: [{ function: patientId, parameters: [$patientId] }]
              ) {
                  logId
                  oldLevel
                  newLevel
                  createdAt
                  component {
                      key
                  }
              }
          }
      `,
        variables: {
          patientId: this._patientId,
        },
      })
      .then((result) => {
        this.myBehaviourComponentLogs = result.data.myBehaviourComponentLogs;
        return this._createJourneyProgress();
      });
  }

  // Creates progress data
  async _createJourneyProgress() {
    let range1 = moment.range(moment(this._enrollmentDate), moment());

    let newstart = range1.start.startOf("isoWeek");
    let newend = range1.end.startOf("isoWeek");
    let range = moment.range(newstart, newend);
    let labels = Array.from(range.by("week"));

    let labelsArray = labels.map((l) => l.format("GGGG-WW"));

    let _journeyProgress = [];

    labelsArray.map((label) => {
      _journeyProgress.push({
        date: label,
        data: [
          {
            // behaviour component
            behaviourChangeGoal: "MEDICATIONS",
            // LOG
            guidance_level: "0",
            // Position based different logic / LOG
            behaviourChangeGoalPosition: 4,
            // active
            active: true,
          },
          {
            behaviourChangeGoal: "STARTMOVING",
            guidance_level: "0",
            behaviourChangeGoalPosition: 4,
            active: true,
          },
          {
            behaviourChangeGoal: "HEALTHYNUTRITION",
            guidance_level: "0",
            behaviourChangeGoalPosition: 4,
            active: true,
          },
          {
            behaviourChangeGoal: "SMOKECESSATION",
            guidance_level: "0",
            behaviourChangeGoalPosition: 4,
            active: true,
          },
          {
            behaviourChangeGoal: "STRESSMANAGEMENT",
            guidance_level: "0",
            behaviourChangeGoalPosition: 4,
            active: true,
          },
        ],
      });
    });

    // Check behaviour change results and change LOG for weeks affected.
    this.myBehaviourComponentLogs.map((changeLOG) => {
      // Get start of the week for the change.
      let changeDate = moment(changeLOG.createdAt).startOf("isoWeek");
      // Get behaviourChangeGoal index.
      let behaviourChangeGoalIndex = 0;
      switch (changeLOG.component.key) {
        case "MEDICATIONS":
          behaviourChangeGoalIndex = 0;
          break;
        case "STARTMOVING":
          behaviourChangeGoalIndex = 1;
          break;
        case "HEALTHYNUTRITION":
          behaviourChangeGoalIndex = 2;
          break;
        case "SMOKECESSATION":
          behaviourChangeGoalIndex = 3;
          break;
        case "STRESSMANAGEMENT":
          behaviourChangeGoalIndex = 4;
          break;
      }
      // find index of week in _journeyProgress
      let indexWeek = _journeyProgress.findIndex(
        (week) => week.date === changeDate.format("GGGG-WW")
      );
      if (indexWeek > 0) {
        // set all following weeks to changed LOG
        for (let i = indexWeek; i <= _journeyProgress.length - 1; i++) {
          _journeyProgress[i].data[behaviourChangeGoalIndex].guidance_level =
            changeLOG.newLevel;
        }
      }
    });

    return this._setMedicationProgressPositions(_journeyProgress);
  }

  async _setMedicationProgressPositions(_journeyProgress) {
    // Gets the adherence percentages for each week
    return this._questionnaireManager
      .getPatientQuestionnaires(
        this._patientId,
        null,
        "MA",
        null,
        null,
        null,
        true,
        true
      )
      .then((questionnaires) => {
        let _overTimeChartBehaviourGoalsDataMA = [];
        questionnaires.map((questionnaire, index) => {
          _overTimeChartBehaviourGoalsDataMA.push({
            x: moment(questionnaire.entryDate).valueOf(),
            y:
              questionnaires[index]?.entries[0]?.givenAnswers[0]?.score,
          });
        });

        this.overTimeChartBehaviourGoalsData[0] = _overTimeChartBehaviourGoalsDataMA;
        return this._setPhysicalActivityProgressPositions(_journeyProgress);
      });
  }

  async _setPhysicalActivityProgressPositions(_journeyProgress) {
    return this._questionnaireManager
      .getPatientQuestionnaires(
        this._patientId,
        null,
        "PA",
        null,
        null,
        null,
        true,
        true
      )
      .then((questionnaires) => {
        let _overTimeChartBehaviourGoalsDataPA = [];
        questionnaires.map((questionnaire, index) => {
          _overTimeChartBehaviourGoalsDataPA.push({
            x: moment(questionnaire.entryDate).valueOf(),
            y:
              parseInt(questionnaires[index]?.entries[0]
                .givenAnswers[0]?.openAnswer),
          })
        });
        this.overTimeChartBehaviourGoalsData[1] = _overTimeChartBehaviourGoalsDataPA;
        // If more behaviourComponents go to next (R2)
        return this._setHealthyNutritionProgressPositions(_journeyProgress);
      });
  }

  async _setHealthyNutritionProgressPositions(_journeyProgress) {
    return this._questionnaireManager
      .getPatientQuestionnaires(
        this._patientId,
        null,
        "HNA",
        null,
        null,
        null,
        true,
        true
      )
      .then((questionnaires) => {
        let _overTimeChartBehaviourGoalsDataHealthyNutrition = [];
        questionnaires.map((questionnaire, index) => {
          _overTimeChartBehaviourGoalsDataHealthyNutrition.push({
            x: moment(questionnaire.entryDate).valueOf(),
            y: questionnaires[index]?.entries[0]?.givenAnswers[0]?.score - 1
          })
        });
        this.overTimeChartBehaviourGoalsData[2] = _overTimeChartBehaviourGoalsDataHealthyNutrition;
        return this._setSmokingCessationProgressPositions(_journeyProgress);
      });
  }

  async _setSmokingCessationProgressPositions(_journeyProgress) {
    return this._questionnaireManager
      .getSmokingHistory(this._patientId)
      .then((questionnairesSMOKINGBEHAVIOUR) => {
        return this._questionnaireManager
          .getPatientQuestionnaires(
            this._patientId,
            null,
            "MOTIVATIONSTOPSMOKING",
            null,
            null,
            null,
            true,
            true
          )
          .then((questionnairesMOTIVATIONSTOPSMOKING) => {
            const questionnairesSMOKINGBEHAVIOURValues = {};
            const questionnairesMOTIVATIONSTOPSMOKINGValues = {};

            questionnairesSMOKINGBEHAVIOUR.forEach((questionnaire, index) => {
              const score = questionnaire?.mappedAnswer === 'YES' ? 0 : 1;

              if (score >= 0){
                questionnairesSMOKINGBEHAVIOURValues[questionnaire.entryDate] = {
                  x : moment(questionnaire.entryDate).valueOf(),
                  y : score
                };
              }
            });

            questionnairesMOTIVATIONSTOPSMOKING.forEach((questionnaire, index) => {
              const score = questionnaire?.score ? questionnaire.score : 0;

              if (score >= 0){
                questionnairesMOTIVATIONSTOPSMOKINGValues[questionnaire.entryDate] = {
                  x : moment(questionnaire.entryDate).valueOf(),
                  y : score
                };
              }
            });

            const _overTimeChartBehaviourGoalsDataSmokingCessationSMOKINGBEHAVIOUR = Object.values(questionnairesSMOKINGBEHAVIOURValues);
            const _overTimeChartBehaviourGoalsDataSmokingCessationMOTIVATIONSTOPSMOKING = Object.values(questionnairesMOTIVATIONSTOPSMOKINGValues);

            // 2 graph datas
            this.overTimeChartBehaviourGoalsData[3] = { left: _overTimeChartBehaviourGoalsDataSmokingCessationSMOKINGBEHAVIOUR, right: _overTimeChartBehaviourGoalsDataSmokingCessationMOTIVATIONSTOPSMOKING };
            return this._setStressReliefProgressPositions(_journeyProgress);
          });
      });
  }

  async _setStressReliefProgressPositions(_journeyProgress) {
    return this._questionnaireManager
      .getPatientQuestionnaires(
        this._patientId,
        null,
        "PERCEIVEDSTRESS",
        null,
        null,
        null,
        true,
        true
      )
      .then((questionnairesPERCEIVEDSTRESS) => {
        return this._questionnaireManager
          .getPatientQuestionnaires(
            this._patientId,
            null,
            "COPINGMEASUREMENTS",
            null,
            null,
            null,
            true,
            true
          )
          .then((questionnairesCOPINGMEASUREMENTS) => {
            const questionnairesPERCEIVEDSTRESSValues = {};
            const questionnairesCOPINGMEASUREMENTSValues = {};

            questionnairesPERCEIVEDSTRESS.forEach((questionnaire, index) => {
              const score = questionnaire?.score ? questionnaire.score : 0;

              if (score >= 0){
                questionnairesPERCEIVEDSTRESSValues[questionnaire.entryDate] = {
                  x : moment(questionnaire.entryDate).valueOf(),
                  y : score
                };
              }
            });

            questionnairesCOPINGMEASUREMENTS.forEach((questionnaire, index) => {
              const score = questionnaire?.score ? questionnaire.score : 0;

              if (score >= 0){
                questionnairesCOPINGMEASUREMENTSValues[questionnaire.entryDate] = {
                  x : moment(questionnaire.entryDate).valueOf(),
                  y : score
                };
              }
            });

            const _overTimeChartBehaviourGoalsDataSmokingCessationPERCEIVEDSTRESS = Object.values(questionnairesPERCEIVEDSTRESSValues);
            const _overTimeChartBehaviourGoalsDataSmokingCessationCOPINGMEASUREMENTS = Object.values(questionnairesCOPINGMEASUREMENTSValues);

            // 2 graph datas
            this.overTimeChartBehaviourGoalsData[4] = { left: _overTimeChartBehaviourGoalsDataSmokingCessationPERCEIVEDSTRESS, right: _overTimeChartBehaviourGoalsDataSmokingCessationCOPINGMEASUREMENTS };

            this.journeyProgress = _journeyProgress;
            this.sliderValue = _journeyProgress.length - 1;
            this.visibleJourneyProgress =
              _journeyProgress[_journeyProgress.length - 1];
          });
      });
  }

  calcMedDietScore(entriesMEDDIET, entriesNUTRITION) {
    let _score = 0;
    entriesMEDDIET.map((entry) => {
      _score = _score + parseInt(entry.givenAnswers[0].score);
    })
    entriesNUTRITION.map((entry) => {
      _score = _score + parseInt(entry.givenAnswers[0].score);
    })
    return _score
  }

  calcScoreQuestionnaire(entriesArray) {
    let _score = 0;
    entriesArray.map((entries) => {
      entries.map((entry) => _score = _score + entry.givenAnswers[0].score)
    })
  }
}
